import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { axios } from 'api'
import { useUserContext } from 'context'
import useToast from 'hooks/useAlertAndToast/useToast'
import { executeRecaptcha, preloadRecaptcha } from 'utils/recaptcha'
import { ContentModal } from 'ui/acro'
import { Box, Button, Flex, H5, P } from 'ui/bend'
import { Input } from 'ui/bend/blocks/FormElements'

import DefaultGuestFields from './DefaultGuestFields'
import EventRegistrationForm from './EventRegistrationForm'
import {
  buildGuestNamePayload,
  buildRegistrationAnswersPayload,
  mergePhoneAnswer,
  questionsForForm,
  registrationDefaultsHasPhone,
  splitRegistrationQuestions,
  validateDefaultName,
  validateDefaultPhone,
  validateQuestions
} from './buildRegistrationAnswers'

const SUCCESS_MESSAGES = {
  registered: "You're registered for this event.",
  waitlisted: "You've been added to the waitlist.",
  pending: 'Your registration is pending approval.'
}

const ResendButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const EventRegistrationModal = ({
  useModal,
  eventId,
  eventTitle,
  registrationQuestions = [],
  registrationDefaults = {},
  submitLabel = 'RSVP',
  onSuccess
}) => {
  const { user } = useUserContext()
  const setToast = useToast()
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [nameFields, setNameFields] = useState(() => ({
    fullName: [user?.firstName, user?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim(),
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  }))
  const [nameErrors, setNameErrors] = useState({})
  const [phoneNumber, setPhoneNumber] = useState(user?.phonenumber || '')
  const [phoneError, setPhoneError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [otpRequired, setOtpRequired] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpError, setOtpError] = useState(null)
  const [otpNotice, setOtpNotice] = useState(null)

  const questions = useMemo(
    () => (Array.isArray(registrationQuestions) ? registrationQuestions : []),
    [registrationQuestions]
  )
  const formQuestions = useMemo(
    () => questionsForForm(questions, registrationDefaults),
    [questions, registrationDefaults]
  )
  const { otherQuestions, termsQuestions } = useMemo(
    () => splitRegistrationQuestions(formQuestions),
    [formQuestions]
  )
  const hasTermsStep = termsQuestions.length > 0
  const totalSteps = hasTermsStep ? 2 : 1
  const hasTwoSteps = totalSteps === 2

  const [step, setStep] = useState(1)

  useEffect(() => {
    preloadRecaptcha()
  }, [])

  const currentQuestions = step === 1 ? otherQuestions : termsQuestions
  const showDefaults = step === 1

  const stepTitle = otpRequired
    ? "Verify it's you"
    : step === 1
    ? 'Registration details'
    : 'Terms & agreements'

  const stepDescription = otpRequired
    ? `For security, enter the 6-digit code we emailed to ${
        user?.email || 'your email'
      }.`
    : step === 1
    ? otherQuestions.length
      ? 'Confirm your account details and answer the questions below.'
      : 'Confirm your account details to continue.'
    : 'Review and accept the terms below to complete your registration.'

  const handleAnswerChange = (questionId, value) => {
    setAnswers((current) => ({ ...current, [questionId]: value }))
    setErrors((current) => {
      if (!current[questionId]) return current
      const next = { ...current }
      delete next[questionId]
      return next
    })
  }

  const validateCurrentStep = () => {
    if (step === 1) {
      const nextErrors = validateQuestions(otherQuestions, answers)
      const nextNameErrors =
        validateDefaultName(registrationDefaults, nameFields) || {}
      const nextPhoneError = validateDefaultPhone(
        registrationDefaults,
        phoneNumber,
        questions
      )
      setErrors(nextErrors)
      setNameErrors(nextNameErrors)
      setPhoneError(nextPhoneError)
      return (
        Object.keys(nextErrors).length === 0 &&
        Object.keys(nextNameErrors).length === 0 &&
        !nextPhoneError
      )
    }

    const nextErrors = validateQuestions(termsQuestions, answers)
    setErrors(nextErrors)
    setPhoneError(null)
    setNameErrors({})
    return Object.keys(nextErrors).length === 0
  }

  const buildPayload = () => {
    const mergedAnswers = mergePhoneAnswer(
      questions,
      answers,
      phoneNumber,
      registrationDefaults
    )
    const payload = {
      registrationAnswers: buildRegistrationAnswersPayload(
        questions,
        mergedAnswers
      ),
      ...buildGuestNamePayload(registrationDefaults, nameFields)
    }
    const trimmedPhone = String(phoneNumber || '').trim()
    if (
      trimmedPhone &&
      registrationDefaultsHasPhone(registrationDefaults) &&
      !questions.some((question) => question.questionType === 'phone-number')
    ) {
      payload.phoneNumber = trimmedPhone
    }
    return payload
  }

  // A low reCAPTCHA score makes the backend respond with `requires_otp` instead
  // of registering. We then email a code and resubmit with `otp` set. Passing an
  // otp value here is what turns a first attempt into a verification attempt.
  const postRegistration = async (otpValue) => {
    setIsSubmitting(true)
    try {
      const payload = buildPayload()
      payload.recaptcha_token = await executeRecaptcha('event_rsvp')
      if (otpValue) payload.otp = otpValue

      const res = await axios.post(`/events/${eventId}/register`, payload)

      if (res.data?.requiresOtp) {
        setOtpRequired(true)
        setOtpError(null)
        setOtpNotice(
          `We emailed a verification code to ${user?.email || 'your email'}.`
        )
        return
      }

      const status = res.data?.registrationStatus || 'registered'
      useModal.close()
      onSuccess?.(status)

      const message = SUCCESS_MESSAGES[status]
      if (message) {
        setToast({ message })
      }
    } catch (error) {
      const data = error?.response?.data
      if (data?.requiresOtp) {
        setOtpRequired(true)
        setOtpNotice(null)
        setOtpError(data.error || 'Incorrect code. Please try again.')
        return
      }

      const message =
        data?.error ||
        error?.response?.message ||
        'Unable to register for this event. Please try again.'
      setToast({ message, error: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePrimaryAction = async () => {
    if (otpRequired) {
      const code = otpCode.trim()
      if (!code) {
        setOtpError('Enter the code we emailed you.')
        return
      }
      await postRegistration(code)
      return
    }

    if (!validateCurrentStep()) return

    if (hasTwoSteps && step === 1) {
      setStep(2)
      setErrors({})
      return
    }

    await postRegistration()
  }

  const resendOtp = async () => {
    setOtpCode('')
    setOtpError(null)
    await postRegistration()
  }

  const handleBack = () => {
    if (otpRequired) {
      setOtpRequired(false)
      setOtpCode('')
      setOtpError(null)
      setOtpNotice(null)
      return
    }

    if (step === 2 && hasTwoSteps) {
      setStep(1)
      setErrors({})
    }
  }

  return (
    <ContentModal
      useModal={useModal}
      overflow='auto'
      style={{
        maxWidth: '560px',
        width: 'calc(100% - 32px)',
        maxHeight: '85vh',
        borderRadius: '0',
        borderWidth: '0'
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <Box py={['24px', '32px']} px={['24px', '32px']}>
        {eventTitle && (
          <P color='grey4' fontSize='14px' mb='8px' mt={0}>
            {eventTitle}
          </P>
        )}

        {hasTwoSteps && !otpRequired && (
          <P color='grey4' fontSize='13px' mb='8px' mt={0}>
            Step {step} of {totalSteps}
          </P>
        )}

        <H5 as='h2' mb='8px' color='primary'>
          {stepTitle}
        </H5>
        <P color='grey4' fontSize='14px' lineHeight='160%' mb='24px' mt={0}>
          {stepDescription}
        </P>

        {otpRequired ? (
          <Box>
            {otpNotice && (
              <P color='grey4' fontSize='13px' mb='16px' mt={0}>
                {otpNotice}
              </P>
            )}
            <Input
              id='rsvp_otp'
              label='Verification code'
              value={otpCode}
              inputMode='numeric'
              autoComplete='one-time-code'
              onChange={(event) => {
                setOtpCode(event.target.value.replace(/[^0-9]/g, ''))
                if (otpError) setOtpError(null)
              }}
            />
            {otpError && (
              <P color='errorDefault' fontSize='13px' mt='8px' mb={0}>
                {otpError}
              </P>
            )}
            <P color='grey4' fontSize='13px' mt='16px' mb={0}>
              Didn&apos;t get a code?{' '}
              <ResendButton
                type='button'
                onClick={resendOtp}
                disabled={isSubmitting}
              >
                Resend
              </ResendButton>
            </P>
          </Box>
        ) : (
          <>
            {showDefaults && (
              <DefaultGuestFields
                user={user}
                registrationDefaults={registrationDefaults}
                nameFields={nameFields}
                nameErrors={nameErrors}
                phoneNumber={phoneNumber}
                phoneError={phoneError}
                onNameChange={(field, value) => {
                  setNameFields((current) => ({ ...current, [field]: value }))
                  if (nameErrors[field]) {
                    setNameErrors((current) => {
                      const next = { ...current }
                      delete next[field]
                      return next
                    })
                  }
                }}
                onPhoneChange={(value) => {
                  setPhoneNumber(value)
                  if (phoneError) setPhoneError(null)
                }}
              />
            )}

            <EventRegistrationForm
              questions={currentQuestions}
              answers={answers}
              errors={errors}
              onAnswerChange={handleAnswerChange}
            />
          </>
        )}

        <Flex
          flexDirection={['column', 'row']}
          style={{ gap: '12px' }}
          mt='32px'
          justifyContent='flex-end'
        >
          {(otpRequired || (step === 2 && hasTwoSteps)) && (
            <Button
              type='button'
              variant='ghost'
              width={['100%', 'auto']}
              minWidth='120px'
              onClick={handleBack}
              disabled={isSubmitting}
            >
              Back
            </Button>
          )}
          <Button
            type='button'
            variant='solid'
            width={['100%', 'auto']}
            minWidth='160px'
            onClick={handlePrimaryAction}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? otpRequired
                ? 'Verifying…'
                : 'Registering...'
              : otpRequired
              ? 'Verify & RSVP'
              : hasTwoSteps && step === 1
              ? 'Continue'
              : submitLabel}
          </Button>
        </Flex>
      </Box>
    </ContentModal>
  )
}

EventRegistrationModal.propTypes = {
  useModal: PropTypes.shape({
    active: PropTypes.bool,
    close: PropTypes.func.isRequired
  }).isRequired,
  eventId: PropTypes.string.isRequired,
  eventTitle: PropTypes.string,
  registrationQuestions: PropTypes.arrayOf(PropTypes.object),
  registrationDefaults: PropTypes.shape({
    nameRequirement: PropTypes.oneOf(['full-name', 'first-last']),
    phoneNumberRequirement: PropTypes.oneOf(['optional', 'required'])
  }),
  submitLabel: PropTypes.string,
  onSuccess: PropTypes.func
}

export default EventRegistrationModal
